import React from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { PrimaryButton } from "src/ui/shared/CustomButton";
import PasswordField from "src/ui/shared/form/PasswordField";
import { ControlledSelectField } from "src/ui/shared/form/SelectField/SelectField";
import { TextField, UncontrolledTextField } from "src/ui/shared/form/TextField";
import { Modal } from "src/ui/shared/Modal/Modal";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { useGetOauthClient } from "@admin/hooks/useGetOauthClient";
import { useUpdateOauthClient } from "@admin/hooks/useUpdateOauthClient";
import { Box, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { parseLines } from "./AdminOauthClientPage.utils";
import { ClientValidationSchema } from "./AdminOauthClientPage.validation";
const editClientFormId = "edit-client-form";

type FormValues = API.OauthClient;

type Props = {
    clientId: string;
    open: boolean;
    onClose: () => void;
    onSuccess: (updatedClient: API.OauthClient) => void;
};

const EditClientModal = (props: Props) => {
    const { data: client, isLoading: isFetching } = useGetOauthClient(props.clientId);
    const { isLoading: isUpdating, onMutate: onUpdate } = useUpdateOauthClient();

    const theme = useTheme();
    const { t } = useTranslation(["common", "pages"]);

    const validate = useValidationSchema(ClientValidationSchema);

    const onSubmit = (values: FormValues) => {
        if (!client) {
            return;
        }

        onUpdate(client.clientId, values, (updatedClient) => {
            props.onSuccess(updatedClient);
            props.onClose();
        });
    };

    return (
        <Modal
            open={props.open}
            onClose={props.onClose}
            title={props.clientId}
            maxWidth="sm"
            fullWidth
            endButton={
                <PrimaryButton type="submit" form={editClientFormId} disabled={isFetching || isUpdating} loading={isUpdating}>
                    {t("common:button.save")}
                </PrimaryButton>
            }
        >
            {isFetching || !client ? (
                <Box display="flex" justifyContent="center" padding={theme.spacing(4)}>
                    <CircularProgress />
                </Box>
            ) : (
                <Form<FormValues>
                    onSubmit={onSubmit}
                    initialValues={client}
                    validate={validate}
                    render={({ handleSubmit, values, form, errors }) => (
                        <form id={editClientFormId} onSubmit={handleSubmit}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <UncontrolledTextField label="Client ID" value={client.clientId} disabled />
                                <TextField name="displayName" label={t("common:label.displayName")} />
                                <PasswordField name="clientSecret" label={t("pages:adminOauthClientsPage.clientSecret")} />
                                <ControlledSelectField
                                    name="clientType"
                                    label={t("pages:adminOauthClientsPage.clientType")}
                                    options={[
                                        { value: "public", label: "Public" },
                                        { value: "confidential", label: "Confidential" },
                                    ]}
                                />
                                <UncontrolledTextField
                                    errorMessage={errors?.redirectUris}
                                    value={values?.redirectUris?.join("\n")}
                                    onChange={(e) => form.change("redirectUris", parseLines(e.target.value))}
                                    label={t("pages:adminOauthClientsPage.redirectUris")}
                                    helperText={t("pages:adminOauthClientsPage.urisHelperText")}
                                    rows={3}
                                />
                                <UncontrolledTextField
                                    errorMessage={errors?.postLogoutRedirectUris}
                                    value={values?.postLogoutRedirectUris?.join("\n")}
                                    onChange={(e) => form.change("postLogoutRedirectUris", parseLines(e.target.value))}
                                    label={t("pages:adminOauthClientsPage.postLogoutRedirectUris")}
                                    helperText={t("pages:adminOauthClientsPage.urisHelperText")}
                                    rows={3}
                                />
                                <UncontrolledTextField
                                    errorMessage={errors?.permissions}
                                    value={values?.permissions?.join("\n")}
                                    onChange={(e) => form.change("permissions", parseLines(e.target.value))}
                                    label={t("pages:adminOauthClientsPage.permissions")}
                                    helperText={t("pages:adminOauthClientsPage.permissionsHelperText")}
                                    rows={3}
                                />
                                <UncontrolledTextField
                                    errorMessage={errors?.requirements}
                                    value={values?.requirements?.join("\n")}
                                    onChange={(e) => form.change("requirements", parseLines(e.target.value))}
                                    label={t("pages:adminOauthClientsPage.requirements")}
                                    helperText={t("pages:adminOauthClientsPage.permissionsHelperText")}
                                    rows={3}
                                />
                            </Box>
                        </form>
                    )}
                />
            )}
        </Modal>
    );
};

export default EditClientModal;
